import { useNavigate, useLocation, useMatch } from "react-router-dom";
import {
  Table,
  useBreakpoints,
  Row,
  TruncatedAccount,
  MenuButton,
} from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import { useLocalization } from "@fluent/react";
import { showAmountStrikeThrough } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import {
  showFileImportCancelOption,
  DisplayStatus,
} from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity";
import type { FormattedFileImportItem } from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity/types";
import {
  showReverseOption,
  hasAnyReversePermission,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useUser } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import {
  useDualApprovalPermission,
  useACHReverseCollectPermission,
  useACHReverseSendPermission,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments/permissions";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { FILTER_CONFIG, FilterChips } from "../Filters/filterConfig";
import {
  PAYMENT_ACTIVITY_ROUTE,
  getFileImportDrawerRoute,
  getFileImportReverseRoute,
  getFileImportReverseFromDrawerRoute,
  getFileImportReversalSelectionRoute,
  getNachaPassthroughDrawerRoute,
  getWireImportDrawerRoute,
} from "../routes";
import { RenderDrawerProps, StatusTag, TableWithDrawer } from "../common";
import styles from "../PaymentActivity.module.scss";
import BulkPaymentDrawer from "../Bulk/BulkPaymentDrawer";
import PassthroughImportDrawer from "./PassthroughImportDrawer";
import type { FileImportCancelTargetId, FileImportCancelType } from "./types";
import WireImportDrawer from "./WireImportDrawer";

const getItemDrawerRoute = (item: FormattedFileImportItem) => {
  switch (item.type) {
    case "ach_passthrough":
      return getNachaPassthroughDrawerRoute(item.uuid);
    case "wire":
      return getWireImportDrawerRoute(item.uuid);
    default:
      return getFileImportDrawerRoute(item.uuid);
  }
};

const FileImportTable = ({
  payments,
  handleToggleCancelDialog,
}: {
  payments: FormattedFileImportItem[];
  handleToggleCancelDialog: (
    uuid: FileImportCancelTargetId,
    type?: FileImportCancelType
  ) => void;
}) => {
  const { m, l } = useBreakpoints();
  const location = useLocation();
  const navigate = useNavigate();
  const passthroughDrawerMatch = useMatch(
    `/${PAYMENT_ACTIVITY_ROUTE}/file-import/ach-passthrough/:fileImportId`
  );
  const wireDrawerMatch = useMatch(
    `/${PAYMENT_ACTIVITY_ROUTE}/file-import/wire/:fileImportId`
  );
  const { l10n } = useLocalization();
  const institution = useInstitutionSettings();
  const hasDualApprovalPermission = useDualApprovalPermission();
  const hasReverseSendPermission = useACHReverseSendPermission();
  const hasReverseCollectPermission = useACHReverseCollectPermission();
  const isReversalFeatureEnabled = Boolean(
    institution.features?.user_initiated_ach_reversals
  );
  const currentUser = useUser();

  const handleOpenDrawer = (item: FormattedFileImportItem) => {
    navigate(`${getItemDrawerRoute(item)}${location.search}`);
  };

  const renderRow = (item: FormattedFileImportItem) => {
    const {
      uuid,
      type,
      title,
      displayStatus,
      institutionAccount,
      netTotalDollarsWithSign,
      paymentCount,
      dualApprovalId,
      dateRange,
      isExpedited,
      hasReversal,
      originatingAccountCount,
    } = item;

    const showCancel = showFileImportCancelOption(item, {
      currentUserUuid: currentUser?.id,
    });
    // only ACH file imports support reversal
    const hasReversePermission = hasAnyReversePermission(
      {
        hasCollectPayments: (item.paymentDebitCount ?? 0) > 0,
        hasSendPayments: (item.paymentCreditCount ?? 0) > 0,
      },
      { hasReverseCollectPermission, hasReverseSendPermission }
    );
    const showReverseButtons =
      type === "ach" &&
      showReverseOption(item, {
        isReversalFeatureEnabled,
        hasReversePermission,
      });
    const showStrikeThrough = showAmountStrikeThrough(displayStatus);
    const menuItems = [];

    if (type === "ach") {
      if (
        displayStatus === DisplayStatus.pending_approval &&
        dualApprovalId &&
        hasDualApprovalPermission
      ) {
        menuItems.push(
          <MenuButton.Item
            key="view-approvals-button"
            label={l10n.getString("label-view-approvals")}
            startIcon="eye"
            onSelect={() =>
              navigate(`/approvals/pending?dualApprovalId=${dualApprovalId}`)
            }
          />
        );
      } else if (paymentCount > 0) {
        menuItems.push(
          <MenuButton.Item
            key="view-payments-button"
            label={l10n.getString("view-payments-cta", {
              total: paymentCount,
            })}
            startIcon="eye"
            onSelect={() => {
              navigate({
                pathname: `/${PAYMENT_ACTIVITY_ROUTE}`,
                search: `?${
                  FILTER_CONFIG[FilterChips.BULK_PAYMENT].queryKey
                }=${uuid}`,
              });
            }}
          />
        );
      }
      if (showReverseButtons) {
        menuItems.push(
          <MenuButton.Item
            key="reverse-all-eligible-button"
            label={l10n.getString("label-reverse-all-eligible")}
            startIcon="rotate-ccw"
            onSelect={() => {
              navigate(getFileImportReverseRoute(uuid as API.BulkPaymentId));
            }}
          />,
          <MenuButton.Item
            key="select-for-reversal-button"
            label={l10n.getString("label-select-for-reversal")}
            startIcon="mouse-pointer"
            onSelect={() => {
              navigate(
                getFileImportReversalSelectionRoute(uuid as API.BulkPaymentId)
              );
            }}
          />
        );
      }
    } else if (type === "wire") {
      if (
        displayStatus === DisplayStatus.pending_approval &&
        dualApprovalId &&
        hasDualApprovalPermission
      ) {
        menuItems.push(
          <MenuButton.Item
            key="view-approvals-button"
            label={l10n.getString("label-view-approvals")}
            startIcon="eye"
            onSelect={() =>
              navigate(`/approvals/pending?dualApprovalId=${dualApprovalId}`)
            }
          />
        );
      } else if (paymentCount > 0) {
        menuItems.push(
          <MenuButton.Item
            key="view-wires-button"
            label={l10n.getString("view-payments-cta", {
              total: paymentCount,
            })}
            startIcon="eye"
            onSelect={() => {
              navigate({
                pathname: `/${PAYMENT_ACTIVITY_ROUTE}`,
                search: `?${
                  FILTER_CONFIG[FilterChips.WIRE_IMPORT_BATCH].queryKey
                }=${uuid}`,
              });
            }}
          />
        );
      }
    }

    if (
      showCancel &&
      (type === "ach" || type === "wire" || type === "ach_passthrough")
    ) {
      const cancelType = type;
      menuItems.push(
        <MenuButton.Item
          key="delete-button"
          label={l10n.getString("label-cancel-payment")}
          startIcon="trash-2"
          onSelect={() => {
            // wires cancel through their dual approval; ACH and pass-through
            // files cancel by their own id
            const cancelId =
              cancelType === "wire" && dualApprovalId
                ? dualApprovalId
                : (uuid as FileImportCancelTargetId);
            handleToggleCancelDialog(cancelId, cancelType);
          }}
        />
      );
    }

    const typeLabel = (() => {
      switch (type) {
        case "wire":
          return l10n.getString("label-method-wire");
        case "ach_passthrough":
          return l10n.getString("label-method-ach-passthrough");
        case "ach":
        case "ach_reversal":
          return l10n.getString("label-method-ach");
        default: {
          const unhandledType: never = type;
          throw new Error(`Unhandled file import type: ${unhandledType}`);
        }
      }
    })();

    return (
      <Table.Row
        key={`table-row__${uuid}`}
        onRowClick={() => handleOpenDrawer(item)}
      >
        <Table.Cell>
          <Row>
            {!(m || l) ? (
              <Row.Item shrink>
                <StatusTag status={displayStatus} isBadge />{" "}
              </Row.Item>
            ) : null}
            <Row.Item>
              <div
                className={cc(styles.ellipsisWrapper, { "margin--left--l": m })}
              >
                <span>{title}</span>
                {hasReversal && (
                  <span className="margin--left--xs narmi-icon-rotate-ccw fontColor--secondary" />
                )}
                <div className="fontSize--s fontColor--secondary">
                  {dateRange}
                  {isExpedited && (
                    <span className="margin--left--xs narmi-icon-zap" />
                  )}
                </div>
              </div>
            </Row.Item>
          </Row>
        </Table.Cell>
        <Table.Cell>
          <StatusTag status={displayStatus} isBadge={false} />
        </Table.Cell>
        <Table.Cell>
          {originatingAccountCount && originatingAccountCount > 1 ? (
            l10n.getString("payment-activity-account-count", {
              count: originatingAccountCount,
            })
          ) : (
            <TruncatedAccount
              name={institutionAccount?.name ?? ""}
              lastFour={institutionAccount?.getMaskedNumber() ?? ""}
            />
          )}
        </Table.Cell>
        <Table.Cell>{typeLabel}</Table.Cell>
        <Table.Cell textAlign={"right" as any}>
          <span
            className={cc({
              [styles.strikeThrough]: showStrikeThrough,
            })}
          >
            {netTotalDollarsWithSign}
          </span>
        </Table.Cell>
        <Table.Cell>
          <div
            style={{ alignSelf: "end" }}
            onClick={(e) => e.stopPropagation()}
          >
            {menuItems.length > 0 && (
              <MenuButton triggerIcon="more-vertical">{menuItems}</MenuButton>
            )}
          </div>
        </Table.Cell>
      </Table.Row>
    );
  };

  const renderDrawer: RenderDrawerProps<FormattedFileImportItem> = (
    payment,
    paymentId,
    { onClose, onNext, onPrev, isOpen }
  ) => {
    if (payment?.type === "ach_passthrough" || passthroughDrawerMatch) {
      return (
        <PassthroughImportDrawer
          isOpen={isOpen}
          item={payment}
          itemUuid={paymentId as unknown as API.NachaPassthrough.SubmissionId}
          handleClose={onClose}
          handleOnNext={onNext}
          handleOnPrev={onPrev}
          handleToggleCancelDialog={(uuid) =>
            handleToggleCancelDialog(uuid, "ach_passthrough")
          }
        />
      );
    }

    if (payment?.type === "wire" || wireDrawerMatch) {
      return (
        <WireImportDrawer
          isOpen={isOpen}
          item={payment}
          itemUuid={paymentId as string}
          handleClose={onClose}
          handleOnNext={onNext}
          handleOnPrev={onPrev}
          handleToggleCancelDialog={handleToggleCancelDialog}
        />
      );
    }

    // wire case is handled above. everything else (ach, ach_reversal, and non-wire deep links
    // where the item isn't in the loaded page yet so `payment` is null) renders the bulk
    // payment drawer, which resolves by falling back to a bulk reversal lookup on the backend
    return (
      <BulkPaymentDrawer
        isOpen={isOpen}
        handleClose={onClose}
        handleOnNext={onNext}
        handleOnPrev={onPrev}
        handleToggleCancelDialog={(uuid) =>
          handleToggleCancelDialog(uuid, "ach")
        }
        payment={null}
        paymentUuid={paymentId as API.BulkPaymentId}
        handleReverseAllEligible={(uuid) =>
          navigate(getFileImportReverseFromDrawerRoute(uuid))
        }
        originalPaymentBasePath="file-import"
        handleSelectForReversal={(uuid) =>
          navigate(getFileImportReversalSelectionRoute(uuid))
        }
      />
    );
  };

  return (
    <TableWithDrawer
      items={payments}
      renderRow={renderRow}
      renderDrawer={renderDrawer}
      basePath={`${PAYMENT_ACTIVITY_ROUTE}/file-import`}
      paramKey="fileImportId"
      getItemRoute={getItemDrawerRoute}
    />
  );
};

export default FileImportTable;
